import { CLIENT_ERROR_CATEGORY, FEATURE_NAMES } from "@/loggers/types";
import { getScvCode, getUserType } from "@/loggers/logger";
import { useUserStore } from "@/store/user/user";
import { ROUTES } from "@/router/constants";

const CLIENT_ERROR_EVENT = "client_error";
const MAX_MESSAGE_LENGTH = 300;
const MAX_STACK_LENGTH = 2000;

/** Current route path, used as a drill-down dimension on the error event. */
export const getCurrentRoute = (): string => {
  try {
    return window.location?.pathname || "unknown";
  } catch {
    return "unknown";
  }
};

// Best-effort path-prefix → feature mapping. Longest matching prefix wins so
// e.g. "/port-congestion/terminal-view" still resolves to PORT_CONGESTION.
const ROUTE_FEATURE_MAP: ReadonlyArray<{ prefix: string; feature: FEATURE_NAMES }> = [
  { prefix: ROUTES.SHIPMENTS, feature: FEATURE_NAMES.MVS_SHIPMENTS },
  { prefix: ROUTES.TRACK_AND_TRACE_WORKSPACE, feature: FEATURE_NAMES.MVS_WORKSPACES },
  { prefix: ROUTES.TRACK_AND_TRACE, feature: FEATURE_NAMES.MVS_SHIPMENTS },
  { prefix: ROUTES.AIR_SHIPMENTS, feature: FEATURE_NAMES.MVS_AIR_SHIPMENTS },
  { prefix: ROUTES.DND, feature: FEATURE_NAMES.MVS_DND },
  { prefix: ROUTES.DND_LEGACY, feature: FEATURE_NAMES.MVS_DND },
  { prefix: ROUTES.DND_NEW, feature: FEATURE_NAMES.MVS_DND },
  { prefix: ROUTES.PORT_CONGESTION, feature: FEATURE_NAMES.MVS_PORT_CONGESTION },
  { prefix: ROUTES.EMISSIONS, feature: FEATURE_NAMES.MVS_EMISSIONS },
  { prefix: ROUTES.RISK_MANAGEMENT, feature: FEATURE_NAMES.MVS_RISK_MANAGEMENT },
  { prefix: ROUTES.REPORTING, feature: FEATURE_NAMES.MVS_REPORTING },
  { prefix: ROUTES.WORKSPACE, feature: FEATURE_NAMES.MVS_WORKSPACES },
  { prefix: ROUTES.CARRIER_PERFORMANCE, feature: FEATURE_NAMES.MVS_CARRIER_PERFORMANCE },
  { prefix: ROUTES.METERING, feature: FEATURE_NAMES.MVS_METERING },
  { prefix: ROUTES.TERMS_AND_CONDITIONS, feature: FEATURE_NAMES.MVS_TERMS_AND_CONDITIONS },
  { prefix: ROUTES.UNAUTHORIZED, feature: FEATURE_NAMES.MVS_UNAUTHORIZED }
];

/**
 * Maps a route path to its best-effort feature for grouping. The dashboard
 * route ("/") is matched exactly to avoid it shadowing every other path.
 */
export const getFeatureForRoute = (route: string): FEATURE_NAMES => {
  if (route === ROUTES.DASHBOARD) {
    return FEATURE_NAMES.MVS_CONTROL_TOWER;
  }

  const match = ROUTE_FEATURE_MAP.filter((entry) => route.startsWith(entry.prefix)).sort(
    (a, b) => b.prefix.length - a.prefix.length
  )[0];

  return match?.feature ?? FEATURE_NAMES.MVS;
};

export interface ClientErrorDetail {
  /** Human-readable error message (will be truncated). */
  message: string;
  /** Origin of the error: a file/url, or "window" | "promise" | "vue:<info>" | "resource". */
  source: string;
  /** JS error type (TypeError, ReferenceError, …); defaults to "Error". */
  errorName?: string;
  /** Stack trace, if the error carried one. Sanitised + truncated before emit. */
  stack?: string;
}

const truncate = (message: string): string =>
  message.length > MAX_MESSAGE_LENGTH ? `${message.slice(0, MAX_MESSAGE_LENGTH)}…` : message;

/**
 * Prepares a stack trace for emission: strips query strings from any embedded
 * URLs (they can carry tokens/PII) and truncates to a stack-appropriate cap.
 * Returns "" when there is no stack, so the attribute can be omitted.
 */
const sanitiseStack = (stack: string): string => {
  if (!stack) {
    return "";
  }
  const stripped = stack.replace(/\?[^\s)]*/g, "");
  return stripped.length > MAX_STACK_LENGTH ? `${stripped.slice(0, MAX_STACK_LENGTH)}…` : stripped;
};

/**
 * Emits a single categorised `client_error` Faro event. Each attribute becomes a
 * queryable field in Loki, so the Grafana dashboards group/alert by `category`
 * (and drill down by `route` / `feature`). No-ops safely when RUM is unavailable.
 */
export const logClientError = (category: CLIENT_ERROR_CATEGORY, detail: ClientErrorDetail): void => {
  if (!window.MOP_RUM?.api?.pushEvent) {
    return;
  }

  try {
    const userStore = useUserStore();
    const route = getCurrentRoute();
    const attributes: Record<string, string> = {
      category,
      errorName: detail.errorName || "Error",
      message: truncate(detail.message || "unknown error"),
      source: detail.source || "unknown",
      route,
      feature: getFeatureForRoute(route),
      userType: getUserType(userStore),
      scvCode: getScvCode(userStore)
    };

    const stack = sanitiseStack(detail.stack || "");
    if (stack) {
      attributes.stack = stack;
    }

    window.MOP_RUM.api.pushEvent(CLIENT_ERROR_EVENT, attributes);
  } catch (e) {
    console.error("MOP_RUM: Failed to publish client error:", e);
  }
};
